import React, { useCallback } from 'react';
import { Loading } from '@moda/om';
import { throwQueryError } from '../../../lib/apollo/throwQueryError';
import { reportError } from '../../../lib/reportError';
import { useErrorModal } from '../../../hooks/useErrorModal';
import {
  useCart,
  CartUpdateAction,
  useUpdateCartMutation,
  CartUpdatePropertyName
} from '../../../hooks/useCart';
import { useCheckoutStepTracking, CheckoutStep } from '../../../hooks/useCheckout';
import { AdyenPaymentMethodSelector, Mode } from '../AdyenPaymentMethodSelector';

interface Props {
  onSaved: () => void;
}

export const CheckoutAdyenPaymentMethod: React.FC<Props> = ({ onSaved }) => {
  const {
    cart: {
      attributes: { paymentMethodId: defaultPaymentMethodId }
    },
    loaded: cartLoaded,
    error: cartError
  } = useCart();
  const [updateCart] = useUpdateCartMutation();
  const { trackCheckoutStepView, trackCheckoutStepComplete } = useCheckoutStepTracking(
    CheckoutStep.PaymentMethod
  );
  const { showErrorModal } = useErrorModal();

  const onSubmit = useCallback(
    async selectedPaymentMethodId => {
      try {
        await updateCart(CartUpdateAction.PaymentMethod, {
          [CartUpdatePropertyName.PaymentMethod]: selectedPaymentMethodId
        });
        trackCheckoutStepComplete();
        onSaved();
      } catch (error) {
        reportError(error);
        showErrorModal();
      }
    },
    [onSaved, updateCart, trackCheckoutStepComplete, showErrorModal]
  );

  const onModeChange = useCallback(
    (mode: Mode) => {
      if (mode === Mode.Selecting) trackCheckoutStepView();
    },
    [trackCheckoutStepView]
  );
  if (!cartLoaded) return <Loading />;
  if (cartError) throwQueryError(cartError);

  return (
    <AdyenPaymentMethodSelector
      isChangeable
      onSubmit={onSubmit}
      onModeChange={onModeChange}
      defaultPaymentMethodId={defaultPaymentMethodId}
    />
  );
};
